"use client"

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from '../../lib/utils'

// Base Tooltip exports - for other composed components
export {
  Tooltip as UITooltipBase,
  TooltipTrigger as UITooltipTriggerBase,
  TooltipContent as UITooltipContentBase,
  TooltipProvider as UITooltipProviderBase
} from '../../components/tooltip'

const tooltipVariants = cva(
  "z-50 max-w-xs rounded-md px-3 py-1.5 text-xs shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
  {
    variants: {
      variant: {
        default: "bg-primary/90 dark:bg-primary/80 text-primary-foreground border border-primary-foreground/20 dark:border-primary-foreground/10 ring-1 ring-white/10 dark:ring-white/5",
        secondary: "bg-secondary/90 dark:bg-secondary/80 text-secondary-foreground border border-gray-200 dark:border-gray-700 ring-1 ring-white/10 dark:ring-white/5",
        accent: "bg-accent/90 dark:bg-accent/80 text-accent-foreground border border-accent-foreground/20 dark:border-accent-foreground/10 ring-1 ring-white/10 dark:ring-white/5",
        destructive: "bg-destructive/90 dark:bg-destructive/80 text-destructive-foreground border border-destructive-foreground/20 dark:border-destructive-foreground/10 ring-1 ring-white/10 dark:ring-white/5",
        outline: "bg-background/95 dark:bg-gray-900/90 text-foreground border border-gray-200 dark:border-gray-700 ring-1 ring-white/10 dark:ring-white/5",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const tooltipArrowVariants = cva(
  "fill-current",
  {
    variants: {
      variant: {
        default: "text-primary",
        secondary: "text-secondary",
        accent: "text-accent",
        destructive: "text-destructive",
        outline: "text-background",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface UITooltipProps extends VariantProps<typeof tooltipVariants> {
  /**
   * 툴팁에 표시될 내용
   */
  content: React.ReactNode
  /**
   * 툴팁이 표시될 대상 요소
   */
  children: React.ReactNode
  /**
   * 툴팁이 표시될 방향
   */
  side?: 'top' | 'right' | 'bottom' | 'left'
  /**
   * 툴팁 정렬 방식
   */
  align?: 'start' | 'center' | 'end'
  /**
   * 툴팁 표시 지연 시간 (밀리초)
   */
  delay?: number
  /**
   * 툴팁 비활성화 여부
   */
  disabled?: boolean
  /**
   * 툴팁과 trigger 간의 거리
   */
  sideOffset?: number
  /**
   * 추가 CSS 클래스
   */
  className?: string
  /**
   * 컨테이너 요소를 지정 (Portal 대상)
   */
  container?: Element | null
  /**
   * 화살표 표시 여부
   */
  showArrow?: boolean
}

/**
 * UITooltip - 사용하기 쉬운 통합 툴팁 컴포넌트
 *
 * Radix UI Tooltip primitive를 기반으로 한 완전한 기능을 제공하는 툴팁입니다.
 * 접근성을 완전히 지원하며 키보드 네비게이션과 스크린 리더를 지원합니다.
 *
 * @example
 * ```tsx
 * <UITooltip content="유용한 정보입니다">
 *   <button>호버하세요</button>
 * </UITooltip>
 * ```
 */
export const UITooltip = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  UITooltipProps
>(({
  content,
  children,
  variant = "default",
  side = "top",
  align = "center",
  delay = 300,
  disabled = false,
  sideOffset = 4,
  className,
  container,
  showArrow = true,
  ...props
}, ref) => {
  // 비활성화된 경우 툴팁 없이 children만 반환
  if (disabled || !content) {
    return <>{children}</>
  }

  return (
    <TooltipPrimitive.Provider delayDuration={delay}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal container={container}>
          <TooltipPrimitive.Content
            ref={ref}
            side={side}
            align={align}
            sideOffset={sideOffset}
            className={cn(
              tooltipVariants({ variant }),
              className
            )}
            {...props}
          >
            {content}
            {showArrow && (
              <TooltipPrimitive.Arrow
                className={tooltipArrowVariants({ variant })}
                width={11}
                height={5}
              />
            )}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
})

UITooltip.displayName = "UITooltip"

